import {FC, ReactNode, useEffect, useState} from 'react'
import {api, ApiError, FormResponses, GoogleStatus, ServiceStatus} from '../../services/api'
import {PageHead} from '../../components/PageHead'
import {Spinner} from '../../components/Spinner'
import {useToast} from '../../components/Toast'

const ACCENT = '#039540'
const INK = '#1b1b29'
const MUTED = '#7e8299'

const statusTone = (s?: ServiceStatus) => (!s ? MUTED : s.ok ? ACCENT : s.enabled ? '#d9214e' : MUTED)
const statusLabel = (s?: ServiceStatus) =>
  !s ? 'Checking…' : s.ok ? 'Connected' : s.enabled ? 'Not reachable' : 'Not configured'

/** One integration card with a status pill, message, and action buttons. */
const Card: FC<{title: string; subtitle: string; status?: ServiceStatus; children?: ReactNode; hint?: ReactNode}> = ({
  title,
  subtitle,
  status,
  children,
  hint,
}) => (
  <div className='ccp-lift' style={{background: '#fff', borderRadius: 16, boxShadow: '0 6px 18px rgba(27,27,41,0.06)', padding: '24px 26px'}}>
    <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap'}}>
      <div>
        <h2 style={{fontSize: 18, fontWeight: 800, color: INK, margin: '0 0 4px'}}>{title}</h2>
        <p style={{fontSize: 14, color: MUTED, margin: 0}}>{subtitle}</p>
      </div>
      <span style={{display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 700, color: statusTone(status), background: `${statusTone(status)}1a`, borderRadius: 999, padding: '6px 14px'}}>
        <span style={{width: 8, height: 8, borderRadius: '50%', background: statusTone(status)}} />
        {statusLabel(status)}
      </span>
    </div>
    <div style={{marginTop: 16, padding: '12px 14px', borderRadius: 10, background: '#f7f8fa', fontSize: 13, color: INK, minHeight: 44}}>
      {status ? status.message : 'Checking connection…'}
    </div>
    {children}
    {hint && <p style={{fontSize: 12.5, color: MUTED, marginTop: 14, lineHeight: 1.6}}>{hint}</p>}
  </div>
)

/**
 * Admin integrations hub (Module 11): Google Calendar (booking sync) and Google
 * Sheets (data export). Drive / Forms land here next.
 */
const IntegrationsPage: FC = () => {
  const toast = useToast()
  const [status, setStatus] = useState<GoogleStatus | null>(null)
  const [busy, setBusy] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [forms, setForms] = useState<FormResponses | null>(null)
  const [loadingForms, setLoadingForms] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const check = async () => {
    setBusy(true)
    setErr(null)
    try {
      setStatus(await api.googleStatus())
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not reach the server')
    } finally {
      setBusy(false)
    }
  }

  useEffect(() => {
    void check()
  }, [])

  const runExport = async () => {
    setExporting(true)
    try {
      const r = await api.exportToSheets()
      toast.success(`Exported ${r.bookings} bookings, ${r.payments} payments, ${r.renters} renters to Google Sheets.`)
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Export failed')
    } finally {
      setExporting(false)
    }
  }

  const loadForms = async () => {
    setLoadingForms(true)
    try {
      setForms(await api.formResponses())
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Could not load responses')
    } finally {
      setLoadingForms(false)
    }
  }

  return (
    <div>
      <PageHead icon='🔌' eyebrow='Settings' title='Integrations' sub='Connect Crew Crashpad to outside services.' />

      {err && (
        <div style={{background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', borderRadius: 10, padding: '12px 16px', fontSize: 14, marginBottom: 16, maxWidth: 760}}>{err}</div>
      )}

      <div style={{display: 'flex', flexDirection: 'column', gap: 18, maxWidth: 760}}>
        <Card
          title='Google Calendar'
          subtitle='Approved bookings are mirrored to your Google Calendar as events.'
          status={status?.calendar}
          hint={status && !status.calendar.enabled
            ? <>To enable: add the service-account <b>GOOGLE_CLIENT_EMAIL</b> + <b>GOOGLE_PRIVATE_KEY</b>, share your calendar with that email, set <b>GOOGLE_CALENDAR_ID</b>, then restart. Steps: <code>backend/docs/google-integrations.md</code>.</>
            : undefined}
        >
          <div style={{display: 'flex', gap: 10, marginTop: 16}}>
            <button onClick={check} disabled={busy} style={{...btnGhost, minWidth: 150}}>
              {busy ? <Spinner size={14} /> : 'Test connection'}
            </button>
          </div>
        </Card>

        <Card
          title='Google Sheets'
          subtitle='Export current bookings, payments and renters to a spreadsheet.'
          status={status?.sheets}
          hint={status && !status.sheets.enabled
            ? <>To enable: create a Google Sheet, share it with the service-account email (<b>Editor</b>), and set <b>GOOGLE_SHEET_ID</b> (the id in the sheet's URL) in <code>.env</code>, then restart.</>
            : <>Each export <b>overwrites</b> the Bookings, Payments and Renters tabs with the latest data.</>}
        >
          <div style={{display: 'flex', gap: 10, marginTop: 16}}>
            <button onClick={runExport} disabled={exporting || !status?.sheets.ok} style={{...btnPrimary, minWidth: 170, opacity: status?.sheets.ok ? 1 : 0.5, cursor: status?.sheets.ok ? 'pointer' : 'not-allowed'}}>
              {exporting ? <Spinner size={14} /> : 'Export to Sheets now'}
            </button>
            <button onClick={check} disabled={busy} style={btnGhost}>
              {busy ? <Spinner size={14} /> : 'Test connection'}
            </button>
          </div>
        </Card>

        <Card
          title='Google Drive'
          subtitle='New file uploads (IDs, receipts, photos) are backed up to a Drive folder.'
          status={status?.drive}
          hint={status && !status.drive.enabled
            ? <>To enable: create a Drive <b>folder</b>, share it with the service-account email (<b>Editor</b>), and set <b>GOOGLE_DRIVE_FOLDER_ID</b> (the id in the folder's URL) in <code>.env</code>, then restart.</>
            : <>Files uploaded after this is connected are copied to Drive automatically — existing files aren't backfilled.</>}
        >
          <div style={{display: 'flex', gap: 10, marginTop: 16}}>
            <button onClick={check} disabled={busy} style={btnGhost}>
              {busy ? <Spinner size={14} /> : 'Test connection'}
            </button>
          </div>
        </Card>

        <Card
          title='Google Forms'
          subtitle='Surface responses from a Google Form (inquiries, sign-ups) in admin.'
          status={status?.forms}
          hint={status && !status.forms.enabled
            ? <>To enable: set <b>GOOGLE_FORM_ID</b> (the id in the form's edit URL) in <code>.env</code> and share the form with the service-account email, then restart.</>
            : undefined}
        >
          <div style={{display: 'flex', gap: 10, marginTop: 16}}>
            <button onClick={loadForms} disabled={loadingForms || !status?.forms.ok} style={{...btnPrimary, minWidth: 160, opacity: status?.forms.ok ? 1 : 0.5, cursor: status?.forms.ok ? 'pointer' : 'not-allowed'}}>
              {loadingForms ? <Spinner size={14} /> : 'Load responses'}
            </button>
            <button onClick={check} disabled={busy} style={btnGhost}>
              {busy ? <Spinner size={14} /> : 'Test connection'}
            </button>
          </div>

          {forms && (
            <div style={{marginTop: 18}}>
              <div style={{fontSize: 13, fontWeight: 700, color: INK, marginBottom: 10}}>
                {forms.title || 'Responses'} · {forms.responses.length} {forms.responses.length === 1 ? 'response' : 'responses'}
              </div>
              {forms.responses.length === 0 ? (
                <p style={{fontSize: 13, color: MUTED, margin: 0}}>No responses yet.</p>
              ) : (
                <div style={{display: 'flex', flexDirection: 'column', gap: 10, maxHeight: 420, overflowY: 'auto'}}>
                  {forms.responses.map((r) => (
                    <div key={r.id} style={{border: '1px solid #eef0f4', borderRadius: 10, padding: '12px 14px'}}>
                      <div style={{fontSize: 11, color: MUTED, marginBottom: 8}}>{r.submittedAt ? new Date(r.submittedAt).toLocaleString() : ''}</div>
                      {r.answers.map((a, i) => (
                        <div key={i} style={{fontSize: 13, marginBottom: 4}}>
                          <span style={{color: MUTED}}>{a.question}: </span>
                          <span style={{color: INK, fontWeight: 600}}>{a.value || '—'}</span>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </Card>
      </div>
    </div>
  )
}

const btnBase: React.CSSProperties = {fontSize: 13, fontWeight: 700, borderRadius: 8, padding: '10px 16px', border: 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer'}
const btnPrimary: React.CSSProperties = {...btnBase, background: ACCENT, color: '#fff'}
const btnGhost: React.CSSProperties = {...btnBase, background: '#fff', border: '1px solid #e5e7eb', color: INK}

export {IntegrationsPage}
